/**
 * useSubscriptionRules Hook
 * 
 * React hook for consuming subscription rules in components.
 * Provides pricing rules based on user's subscription status.
 * 
 * @module useSubscriptionRules
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/lib/auth';
import { useCurrentSubscription } from '@/hooks/useCurrentSubscription';
import {
  fetchPricingConfig,
  fetchTierOverrides,
  getCachedConfigSync,
  getCachedTierOverrideSync,
  type ProductType,
} from '@/lib/pricing-config';
import {
  derivePricingRules,
  getTierFromSubscription,
  calculatePricing,
  DEFAULT_CONFIG,
  type PricingRules,
  type PricingConfig,
  type PricingResult,
} from '@/lib/subscription-rules';
import type { UserSubscription } from '@/types/subscription';

// ============================================
// Types
// ============================================

export interface UseSubscriptionRulesOptions {
  hotelId?: string;
  category?: string;
  product?: ProductType;
}

export interface UseSubscriptionRulesResult {
  rules: PricingRules;
  config: PricingConfig;
  isLoading: boolean;
  error: Error | null;
  subscription: UserSubscription | null;
  calculatePrice: (apiPrice: number) => PricingResult;
  refetch: () => Promise<void>;
}

// ============================================
// Hook Implementation
// ============================================

/**
 * Hook to get subscription rules for the current user
 * 
 * @param options - Optional hotel/category context for overrides
 * @returns Subscription rules, loading state, and pricing calculator
 * 
 * @example
 * ```tsx
 * const { rules, calculatePrice, isLoading } = useSubscriptionRules({ hotelId: 'hotel_123' });
 * 
 * if (isLoading) return <Spinner />;
 * 
 * const pricing = calculatePrice(100);
 * // pricing.standardPrice = 130.20
 * // pricing.specialPrice = 104.00
 * ```
 */
export function useSubscriptionRules(
  options?: UseSubscriptionRulesOptions
): UseSubscriptionRulesResult {
  const { user } = useAuth();
  const product = options?.product;

  // VOY-610: the subscription comes from the shared hook, which refetches once the session is
  // attached and keeps the last known plan when a lookup fails.
  const {
    subscription,
    isLoading: subscriptionLoading,
    error: subscriptionError,
    refetch: refetchSubscription,
  } = useCurrentSubscription(user?.id ?? null);
  const tier = getTierFromSubscription(subscription).id;

  // VOY-512 (D1): seed the initial config from the PRODUCT-specific cache, matching the
  // async fetch below (fetchPricingConfig(product)). Seeding without the product
  // resolved 'all' (25%) on first paint, so a flight card flashed a 25% Standard strikethrough
  // until the async flight fetch landed — worse after browsing hotels warmed the 'all' cache.
  const [config, setConfig] = useState<PricingConfig>(getCachedConfigSync(product));
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState<Error | null>(null);

  const loadConfig = useCallback(async () => {
    setConfigLoading(true);
    try {
      setConfig(await fetchPricingConfig(product));
      setConfigError(null);
    } catch (err) {
      console.error('[useSubscriptionRules] Error fetching data:', err);
      setConfigError(err instanceof Error ? err : new Error('Failed to fetch subscription data'));
      // Fall back to defaults on error
      setConfig(DEFAULT_CONFIG);
    } finally {
      setConfigLoading(false);
    }
  }, [product]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // VOY-606: the tier's DB override, scoped by product like the backend: (tier, product) →
  // (tier, 'all'). Stored with the key it was fetched for, so a tier change never applies the
  // previous tier's row; looked up only once the plan is known, so no 'free' row is fetched
  // for a paying user on the way.
  const overrideKey = `${tier}:${product ?? 'all'}`;
  const [fetchedOverride, setFetchedOverride] = useState<{
    key: string;
    override: Partial<PricingRules> | null;
  } | null>(null);

  useEffect(() => {
    if (subscriptionLoading) return;
    let cancelled = false;
    fetchTierOverrides(tier, { product })
      .then((override) => {
        if (!cancelled) setFetchedOverride({ key: overrideKey, override });
      })
      .catch(() => {
        if (!cancelled) setFetchedOverride({ key: overrideKey, override: null });
      });
    return () => {
      cancelled = true;
    };
  }, [tier, product, overrideKey, subscriptionLoading]);

  const overrideLoading = fetchedOverride?.key !== overrideKey;
  const tierOverride = overrideLoading ? null : fetchedOverride.override;

  // Derive rules from subscription and config
  const rules = useMemo(() => {
    return derivePricingRules(subscription, config, {
      hotelId: options?.hotelId,
      category: options?.category,
      tierOverride: tierOverride ?? undefined,
    });
  }, [subscription, config, tierOverride, options?.hotelId, options?.category]);

  // Memoized price calculator
  const calculatePrice = useCallback(
    (apiPrice: number): PricingResult => {
      return calculatePricing(apiPrice, rules, config);
    },
    [rules, config]
  );

  const refetch = useCallback(async () => {
    await Promise.all([refetchSubscription(), loadConfig()]);
  }, [refetchSubscription, loadConfig]);

  return {
    rules,
    config,
    isLoading: subscriptionLoading || configLoading || overrideLoading,
    error: configError ?? subscriptionError,
    subscription,
    calculatePrice,
    refetch,
  };
}

/**
 * Lightweight hook that only returns rules without loading state
 * Uses cached config - useful for quick rendering
 * 
 * @param options - Optional hotel/category context
 * @returns Just the pricing rules (no loading state)
 */
export function useSubscriptionRulesSync(
  options?: UseSubscriptionRulesOptions
): PricingRules {
  return useSubscriptionRulesSyncState(options).rules;
}

/**
 * useSubscriptionRulesSync plus whether the user's plan is known. Upsell surfaces use it to
 * stay hidden until a lookup succeeds, so a paying user never shows as Free while the plan is
 * loading or its lookup failed (VOY-610).
 */
export function useSubscriptionRulesSyncState(
  options?: UseSubscriptionRulesOptions
): { rules: PricingRules; isPlanKnown: boolean } {
  const { user } = useAuth();
  // VOY-610: shared lookup, refetched on auth events; a failed lookup keeps the known plan.
  const { subscription, isKnown } = useCurrentSubscription(user?.id ?? null);
  const tier = getTierFromSubscription(subscription).id;
  const product = options?.product;
  const hotelId = options?.hotelId;
  const category = options?.category;
  const overrideKey = `${tier}:${product ?? 'all'}`;

  // VOY-606: the override is stored with the (tier, product) it was fetched for, so a
  // tier change (e.g. the subscription loading) never applies the previous tier's row.
  const [fetched, setFetched] = useState<{ key: string; override: Partial<PricingRules> | null } | null>(null);

  const cachedOverride = getCachedTierOverrideSync(tier, { product });

  useEffect(() => {
    if (cachedOverride !== undefined) return;
    let cancelled = false;
    fetchTierOverrides(tier, { product })
      .then((override) => {
        if (!cancelled) setFetched({ key: overrideKey, override });
      })
      .catch(() => {
        if (!cancelled) setFetched({ key: overrideKey, override: null });
      });
    return () => {
      cancelled = true;
    };
  }, [tier, product, overrideKey, cachedOverride]);

  const tierOverride =
    cachedOverride !== undefined
      ? cachedOverride
      : fetched?.key === overrideKey
        ? fetched.override
        : null;

  const config = getCachedConfigSync(product);

  const rules = useMemo(() => {
    return derivePricingRules(subscription, config, {
      hotelId,
      category,
      tierOverride: tierOverride ?? undefined,
    });
  }, [subscription, config, tierOverride, hotelId, category]);

  return { rules, isPlanKnown: isKnown };
}

export default useSubscriptionRules;


